import Link from 'next/link';
import classNames from 'classnames';
import {useI18n, LocalizedHtml} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {SubscriptionPlanSubscriberState} from '@udemy-v2/subscription-graphql';
import {BlockList} from '@udemy-v2/react-core-components';
import {
    GwgSubscriptionCTA,
    GwgCheckoutProvider,
    SubscribeFromPageCookie,
    SubscriptionCTA,
} from '@udemy-v2/react-subscription-browse';
import {useGwgPlanPreference} from '@/features/course-landing-page/hooks/use-gwg-plan-preference';
import {CourseContentPlacement} from '@/features/course-landing-page/course-landing-page.react-component';
import styles from './grow-with-google-enrollment-box.module.scss';
import {useClpSubscriptionContext} from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import {useClpCourse} from '../../context/clp-course-provider.react-component';
import {useClpPurchaseContext} from '../../context/clp-purchase-context.react-component';

interface GrowWithGoogleEnrollmentBoxProps {
    placement: CourseContentPlacement;
}

/**
 * GrowWithGoogleCTA component renders the subscription CTA for Grow with Google courses.
 * It uses the GwgSubscriptionCTA component which handles plan precedence logic internally.
 */
const GWG_TIER_UPGRADE_URL =
    '/subscription-checkout/upgrade/?upgrade_type=tier&product_type=CONSUMERSUBSCRIPTION_PLUS_GROW_WITH_GOOGLE';

function GrowWithGoogleCTA() {
    const {gettext} = useI18n();
    const course = useClpCourse();
    const {subscribedConsumerSubscriptionPlan, isValuePlanSubscriber} = useClpSubscriptionContext();
    const {gwgPlan, gwgPlusPersonalPlan, subscriptionTiersEnabled} = useClpPurchaseContext();
    const gwgPlanPreference = useGwgPlanPreference();

    // When gwgPlan is still loading but the combined plan has resolved, pass undefined
    // so GwgSubscriptionCTA can render the upgrade CTA without showing a loading skeleton.
    const plan =
        gwgPlan?.isLoading && gwgPlusPersonalPlan?.isLoading === false ? undefined : gwgPlan;

    const handleCtaClick = () => {
        SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
    };

    // Starter Plan subscribers upgrading to PP+GWG will use a dedicated upgrade checkout URL.
    // GwgSubscriptionCTA doesn't handle this case, so we render SubscriptionCTA directly.
    if (subscriptionTiersEnabled && isValuePlanSubscriber) {
        return (
            <div className={styles['cta-container']}>
                <SubscriptionCTA
                    plan={gwgPlusPersonalPlan}
                    noticeData={{cta_1_url: GWG_TIER_UPGRADE_URL}}
                    courseId={parseInt(course.id, 10)}
                    uiRegion="purchase_section"
                    offerCtaStyle="primary"
                    className={styles['get-started-button']}
                    purchaseCtaText={gettext('Upgrade your plan')}
                    directCheckout={true}
                    onCTAClick={handleCtaClick}
                />
            </div>
        );
    }

    return (
        <GwgCheckoutProvider planPreference={gwgPlanPreference}>
            <div className={styles['cta-container']}>
                <GwgSubscriptionCTA
                    plan={plan}
                    currentlySubscribedPlan={
                        subscribedConsumerSubscriptionPlan &&
                        subscribedConsumerSubscriptionPlan.subscribedPlan.productType
                            ? ({
                                  isSubscriber: true,
                                  isLoading: false,
                                  productType:
                                      subscribedConsumerSubscriptionPlan.subscribedPlan.productType,
                              } as SubscriptionPlanSubscriberState)
                            : undefined
                    }
                    combinedGwgPlan={gwgPlusPersonalPlan}
                    courseId={parseInt(course.id, 10)}
                    courseTrackingId={course.trackingId}
                    uiRegion="purchase_section"
                    offerCtaStyle="primary"
                    className={styles['get-started-button']}
                    planPeriodText={gettext('Billed monthly or annually. Cancel anytime.')}
                    onCTAClick={handleCtaClick}
                />
            </div>
        </GwgCheckoutProvider>
    );
}

/**
 * GrowWithGoogleEnrollmentBox component displays information about courses that are part of
 * the 'Learn AI with Google' subscription plan. Includes a notice, pricing, incentives, and enrollment CTA.
 */
export function GrowWithGoogleEnrollmentBox({placement}: GrowWithGoogleEnrollmentBoxProps) {
    const {gettext, interpolate} = useI18n();
    const isSidebarPlacement = CourseContentPlacement.SIDEBAR === placement;
    const {gwgPlan, gwgPlusPersonalPlan} = useClpPurchaseContext();

    // Get the price from the available plan (prefer gwg only plan if available)
    const plan = gwgPlan || gwgPlusPersonalPlan;

    // Extract formatted monthly price from plan if available
    const formattedPrice =
        plan && 'formattedMonthlyAmount' in plan ? plan.formattedMonthlyAmount : undefined;

    return (
        <div className={styles['grow-with-google-enrollment-box']}>
            <h3 className={classNames('ud-heading-xl', styles['title'])}>
                {gettext('Purchase options')}
            </h3>
            <div
                className={classNames(styles.container, {
                    [styles['container--with-margin']]: !isSidebarPlacement,
                })}
                data-purpose="grow-with-google-enrollment-box"
            >
                <div className={styles['notice-container']}>
                    <div className={styles['icon-container']}>
                        <Icon glyph="warning" size="medium" color="info" label={false} />
                    </div>
                    <div className={styles['notice-content']}>
                        <h3 className={classNames('ud-heading-xs', styles['notice-title'])}>
                            {gettext("Part of the 'Learn AI with Google' plan")}
                        </h3>
                        <p className={classNames('ud-text-xs', styles['notice-description'])}>
                            {gettext(
                                'This course can only be purchased as part of a subscription offering. ',
                            )}
                            <Link
                                href="/partner/google/learn-ai/"
                                className={classNames('ud-text-xs', styles['learn-more-link'])}
                                target="_self"
                            >
                                {gettext('Learn more')}
                            </Link>
                        </p>
                    </div>
                </div>

                {formattedPrice && (
                    <div className={styles['price-container']}>
                        <LocalizedHtml
                            html={interpolate(
                                gettext(
                                    '<span class="label">From</span> <span class="value">%(price)s</span> <span class="period">/month</span>',
                                ),
                                {price: formattedPrice},
                                true,
                            )}
                            interpolate={{
                                label: (
                                    <span
                                        className={classNames(
                                            'ud-heading-sm',
                                            styles['price-label'],
                                        )}
                                    />
                                ),
                                value: (
                                    <span
                                        className={classNames(
                                            'ud-heading-xl',
                                            styles['price-value'],
                                        )}
                                    />
                                ),
                                period: (
                                    <span
                                        className={classNames('ud-text-sm', styles['price-period'])}
                                    />
                                ),
                            }}
                        />
                    </div>
                )}

                <div className={styles['incentives-container']}>
                    <BlockList size="small" padding="tight">
                        <BlockList.Item
                            icon={
                                <Icon
                                    glyph="sparkles"
                                    size="xsmall"
                                    color="neutral"
                                    label={false}
                                    aria-hidden="true"
                                />
                            }
                        >
                            <span className="ud-text-sm">{gettext('3 career learning paths')}</span>
                        </BlockList.Item>
                        <BlockList.Item
                            icon={
                                <Icon
                                    glyph="certificate"
                                    size="xsmall"
                                    color="neutral"
                                    label={false}
                                    aria-hidden="true"
                                />
                            }
                        >
                            <span className="ud-text-sm">
                                {gettext('20+ activities for real-world practice')}
                            </span>
                        </BlockList.Item>
                        <BlockList.Item
                            icon={
                                <Icon
                                    glyph="verified"
                                    size="xsmall"
                                    color="neutral"
                                    label={false}
                                    aria-hidden="true"
                                />
                            }
                        >
                            <span className="ud-text-sm">
                                {gettext('Industry-recognized credentials from Google')}
                            </span>
                        </BlockList.Item>
                        <BlockList.Item
                            icon={
                                <Icon
                                    glyph="expand-plus"
                                    size="xsmall"
                                    color="neutral"
                                    label={false}
                                    aria-hidden="true"
                                />
                            }
                        >
                            <span className="ud-text-sm">
                                <LocalizedHtml
                                    html={interpolate(
                                        gettext(
                                            'Access to Google AI Pro for 3 months at no cost (<a class="terms-link">terms apply</a>)',
                                        ),
                                        true,
                                    )}
                                    interpolate={{
                                        'terms-link': (
                                            <a
                                                href={`/terms/learn-with-google/`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            />
                                        ),
                                    }}
                                />
                            </span>
                        </BlockList.Item>
                    </BlockList>
                </div>

                <GrowWithGoogleCTA />
            </div>
        </div>
    );
}
